import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { friendRemove, getFriends, type Friend } from '../transport/api'
import { Icon } from '../ui/Icon'
import { Sheet } from '../ui/Sheet'
import { personBackground, personLetter } from '../identity/personAvatar'
import { ReportForm, ReportGuidance } from './ReportSection'
import { BlockConfirm, BlockedSection } from '../friends/Block'

/**
 * FRIENDS — the friends list and its per-friend sheet. Split out of ServerPanel.tsx
 * in M6 (拆面板, 2026-09-03), moved verbatim; see MachinesScreen.tsx for the why.
 */
export function FriendsSection() {
  const qc = useQueryClient()
  const friendsQ = useQuery({ queryKey: ['friends'], queryFn: getFriends })
  /*
   * The ROW is the button, exactly like Machines (owner, 2026-08-19: 把 Friends
   * 的 Remove 也改為同樣的彈出選單的方式). Tapping a friend opens their sheet —
   * identity up top (Google avatar, name, email), Remove at the bottom, still
   * behind the DOUBLE confirm.
   */
  const [open, setOpen] = useState<string | null>(null)

  const friends: Friend[] = friendsQ.data?.friends ?? []
  // By ACCOUNT, not address — owner, 2026-10-31: friends with both accounts at one address, every tap opened
  // the first of the two and Remove was refused as ambiguous_account. `account` is the friend list's handle.
  const idOf = (f: Friend) => f.account ?? f.email
  const opened = friends.find((f) => idOf(f) === open) ?? null
  return (
    <>
      <div className="gh-sec">Friends</div>
      {friendsQ.isPending && <p className="gh-note">Checking…</p>}
      {friendsQ.isError && <p className="gh-note">Could not list friends — {String(friendsQ.error)}</p>}
      {!friendsQ.isPending && !friendsQ.isError && friends.length === 0 && (
        <p className="gh-note">No friends yet — add someone from the ＋ button.</p>
      )}
      {friends.map((f) => {
        const name = f.name ?? f.email
        return (
          <button
            type="button"
            className="mch-row"
            key={idOf(f)}
            aria-label={`Manage ${name}`}
            onClick={() => setOpen(idOf(f))}
          >
            {/* The Google avatar on the ROW too (owner, 2026-08-19: 頭像也加到
                Friend List 裡面) — same source and fallback as the sheet, at
                row scale. */}
            {f.picture ? (
              <img
                className="frs-pic frs-pic-row"
                src={f.picture}
                alt=""
                referrerPolicy="no-referrer"
                width={28}
                height={28}
              />
            ) : (
              /* The email's first letter on the person's own colours (owner,
                 2026-09-13 — identity/personAvatar.ts), the same tile every surface draws. */
              <span
                className="frs-pic frs-pic-row frs-fallback"
                style={{ background: personBackground(f.email) }}
                aria-hidden="true"
              >
                {personLetter(f.email, name)}
              </span>
            )}
            <span className="mch-meta">
              <span className="mch-name" title={f.email}>
                {name}
              </span>
            </span>
            <Icon name="chevron_right" size={16} />
          </button>
        )
      })}
      {opened && (
        // Keyed so switching friends remounts the sheet — an armed confirm for one
        // person must never carry into another's Remove.
        <FriendSheet
          key={idOf(opened)}
          friend={opened}
          onClose={() => setOpen(null)}
          onChanged={() => void qc.invalidateQueries({ queryKey: ['friends'] })}
        />
      )}
      <BlockedSection />
    </>
  )
}

/**
 * One friend's sheet: who they are, then the way out — reached by tapping the row.
 *
 * DOUBLE confirmation kept from the row era, stronger than Machines' single arm:
 * Remove → "Remove X?" → "Really? both lose it" → fires, each step with a Cancel
 * and a 4s idle auto-disarm so a half-finished tap never lingers armed. The
 * removal itself is one-sided + silent (the BFF dissolves the mutual edge); the
 * friction only exists to stop a mis-tap (owner: 記得要 double confirm).
 */
function FriendSheet({
  friend,
  onClose,
  onChanged,
}: {
  friend: Friend
  onClose: () => void
  onChanged: () => void
}) {
  const name = friend.name ?? friend.email
  // 0 = resting, 1 = armed ("Remove X?"), 2 = final ("Really?").
  const [step, setStep] = useState<0 | 1 | 2>(0)
  /*
   * REPORTING FROM THE PERSON'S OWN SHEET — avg-devapp-appstore, 2026-09-15.
   *
   * Guideline 1.2 wants a way to flag content, and reviewers look for it AT the
   * content rather than only in settings; a form reachable only from the bottom of a
   * menu invites "we were unable to locate the reporting mechanism". This is the SAME
   * ReportForm the menu renders — one more door, not a second flow — and it hands the
   * form `about`, so the row records WHO without the person having to spell it out.
   *
   * It sits beside Remove deliberately. They are the two things wanted when someone
   * has behaved badly, they are different actions (one stops them now, one reaches
   * us), and having both in one place is what stops a person doing only the one they
   * happened to find.
   */
  const [reporting, setReporting] = useState(false)
  // BLOCK (owner, 2026-10-17, App Store 1.2) — beside Remove and Report, its own confirm.
  const [blocking, setBlocking] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const remove = useMutation({
    mutationFn: () => friendRemove(friend.email, friend.account),
    onSuccess: () => {
      onChanged()
      onClose()
    },
    onError: (e) => setErr(String(e instanceof Error ? e.message : e)),
  })
  const arm = (n: 1 | 2) => {
    setErr(null)
    setStep(n)
    window.setTimeout(() => setStep((s) => (s === n ? 0 : s)), 4000)
  }

  return (
    <Sheet title={name} onClose={onClose} className="mch-sheet">
      <div className="frs-id">
        {/* Google's own avatar when there is one (owner: 把朋友的 Google Account
            頭像放到這個選單上面) — same no-referrer rule as the account block. An
            account that never set a picture gets their initial, not a broken img. */}
        {friend.picture ? (
          <img
            className="frs-pic"
            src={friend.picture}
            alt=""
            referrerPolicy="no-referrer"
            width={56}
            height={56}
          />
        ) : (
          <span
            className="frs-pic frs-fallback"
            style={{ background: personBackground(friend.email) }}
            aria-hidden="true"
          >
            {personLetter(friend.email, name)}
          </span>
        )}
        <div className="frs-who">
          {friend.name ? <span className="frs-name">{friend.name}</span> : null}
          <span className="frs-mail">{friend.email}</span>
          {typeof friend.since === 'number' && friend.since > 0 ? (
            <span className="frs-since">
              Friends since {new Date(friend.since * 1000).toLocaleDateString()}
            </span>
          ) : null}
        </div>
      </div>

      {reporting ? (
        <div className="mchs-block frs-report">
          <ReportGuidance />
          <ReportForm about={`friend:${friend.email}`} aboutLabel={name} />
          <button type="button" className="mch-btn" onClick={() => setReporting(false)}>
            Back
          </button>
        </div>
      ) : blocking ? (
        <div className="mchs-block">
          <BlockConfirm
            name={name}
            email={friend.email}
            account={friend.account}
            onCancel={() => setBlocking(false)}
            onBlocked={() => {
              onChanged()
              onClose()
            }}
          />
        </div>
      ) : (
      <div className="mchs-block">
        {step === 0 ? (
          <>
          <button
            type="button"
            className="mch-btn"
            disabled={remove.isPending}
            onClick={() => arm(1)}
          >
            Remove
          </button>
          <button type="button" className="mch-btn" onClick={() => setReporting(true)}>
            Report
          </button>
          <button type="button" className="mch-btn" onClick={() => setBlocking(true)}>
            Block
          </button>
          </>
        ) : (
          <span className="mch-confirm">
            <button
              type="button"
              className="mch-btn danger"
              disabled={remove.isPending}
              onClick={() => (step === 1 ? arm(2) : remove.mutate())}
            >
              {step === 1 ? `Remove ${name}?` : 'Really? both lose it'}
            </button>
            <button
              type="button"
              className="mch-btn"
              disabled={remove.isPending}
              onClick={() => setStep(0)}
            >
              Cancel
            </button>
          </span>
        )}
      </div>
      )}

      {err && <p className="gh-note mch-err">{err}</p>}
    </Sheet>
  )
}

/**
 * Say why a mount failed in words rather than in the server's error code.
 *
 * The raw codes are accurate and useless on screen: a rig hitting a machine that
 * was never set up reports `not_registered`, which tells someone reading it
 * nothing about what to do. These are the ones a mount actually produces —
 * anything else falls through verbatim rather than being flattened into a
 * friendly lie, because an unrecognised failure is worth seeing exactly.
 */
